import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDryRunChangeSet, useSubmitChangeSet } from './queries';
import { buildServiceChangeSet } from './api';
import { useAuthStore } from '../../store/authStore';
import type { CreateApiForm, DryRunResponse } from './types';
import { defaultCreateApiForm, CreateApiFormSchema } from './schemas';

// Step components

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step <= currentStep
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-600'
            }`}
          >
            {step}
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-12 h-0.5 ${
                step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ServiceStep({ 
  form, 
  onFormChange, 
  errors 
}: { 
  form: CreateApiForm; 
  onFormChange: (form: CreateApiForm) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('apis.wizard.serviceStep.title', 'Service Configuration')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('apis.wizard.serviceStep.description', 'Configure the backend service that Kong will proxy to.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apis.wizard.serviceName', 'Service Name')}
          </label>
          <input
            type="text"
            value={form.service.name}
            onChange={(e) => onFormChange({
              ...form,
              service: { ...form.service, name: e.target.value }
            })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.serviceName ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="telemetry-api"
          />
          {errors.serviceName && (
            <p className="text-red-600 text-sm mt-1">{errors.serviceName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apis.wizard.protocol', 'Protocol')}
          </label>
          <select
            value={form.service.protocol}
            onChange={(e) => onFormChange({
              ...form,
              service: { ...form.service, protocol: e.target.value as 'http' | 'https' }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="http">HTTP</option>
            <option value="https">HTTPS</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apis.wizard.host', 'Host')}
          </label>
          <input
            type="text"
            value={form.service.host}
            onChange={(e) => onFormChange({
              ...form,
              service: { ...form.service, host: e.target.value }
            })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              errors.serviceHost ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="telemetry-api.svc"
          />
          {errors.serviceHost && (
            <p className="text-red-600 text-sm mt-1">{errors.serviceHost}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apis.wizard.port', 'Port')}
          </label>
          <input
            type="number"
            min="1"
            max="65535"
            value={form.service.port}
            onChange={(e) => onFormChange({
              ...form,
              service: { ...form.service, port: parseInt(e.target.value) || 8080 }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apis.wizard.path', 'Base Path')}
          </label>
          <input
            type="text"
            value={form.service.path}
            onChange={(e) => onFormChange({
              ...form,
              service: { ...form.service, path: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="/"
          />
          <p className="text-gray-500 text-xs mt-1">
            {t('apis.wizard.pathHelp', 'The path to append to requests when proxying to the upstream service')}
          </p>
        </div>
      </div>
    </div>
  );
}

function RouteStep({ 
  form, 
  onFormChange, 
  errors 
}: { 
  form: CreateApiForm; 
  onFormChange: (form: CreateApiForm) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();

  const addPath = () => {
    onFormChange({
      ...form,
      route: {
        ...form.route,
        paths: [...form.route.paths, '/api/']
      }
    });
  };

  const removePath = (index: number) => {
    onFormChange({
      ...form,
      route: {
        ...form.route,
        paths: form.route.paths.filter((_, i) => i !== index)
      }
    });
  };

  const updatePath = (index: number, value: string) => {
    const newPaths = [...form.route.paths];
    newPaths[index] = value;
    onFormChange({
      ...form,
      route: { ...form.route, paths: newPaths }
    });
  };

  const toggleMethod = (method: string) => {
    const methods = form.route.methods.includes(method)
      ? form.route.methods.filter(m => m !== method)
      : [...form.route.methods, method];
    
    onFormChange({
      ...form,
      route: { ...form.route, methods }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('apis.wizard.routeStep.title', 'Route Configuration')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('apis.wizard.routeStep.description', 'Define the paths and methods that Kong will match.')}
        </p>
      </div>

      {/* Paths */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('apis.wizard.paths', 'Paths')}
        </label>
        <div className="space-y-2">
          {form.route.paths.map((path, index) => (
            <div key={index} className="flex space-x-2">
              <input
                type="text"
                value={path}
                onChange={(e) => updatePath(index, e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="/api/service"
              />
              {form.route.paths.length > 1 && (
                <button
                  onClick={() => removePath(index)}
                  className="px-3 py-2 text-red-600 hover:text-red-800 focus:outline-none"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addPath}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            + {t('apis.wizard.addPath', 'Add Path')}
          </button>
        </div>
        {errors.paths && (
          <p className="text-red-600 text-sm mt-1">{errors.paths}</p>
        )}
      </div>

      {/* Methods */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('apis.wizard.methods', 'HTTP Methods')}
        </label>
        <div className="flex flex-wrap gap-2">
          {['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'].map((method) => (
            <button
              key={method}
              onClick={() => toggleMethod(method)}
              className={`px-3 py-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                form.route.methods.includes(method)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {method}
            </button>
          ))}
        </div>
        {errors.methods && (
          <p className="text-red-600 text-sm mt-1">{errors.methods}</p>
        )}
      </div>

      {/* Advanced Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={form.route.strip_path}
            onChange={(e) => onFormChange({
              ...form,
              route: { ...form.route, strip_path: e.target.checked }
            })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            {t('apis.wizard.stripPath', 'Strip path from upstream')}
          </span>
        </label>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={form.route.preserve_host}
            onChange={(e) => onFormChange({
              ...form,
              route: { ...form.route, preserve_host: e.target.checked }
            })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            {t('apis.wizard.preserveHost', 'Preserve host header')}
          </span>
        </label>
      </div>
    </div>
  );
}

function PoliciesStep({ 
  form, 
  onFormChange 
}: { 
  form: CreateApiForm; 
  onFormChange: (form: CreateApiForm) => void;
}) {
  const { t } = useTranslation();

  const updateCorsOrigins = (value: string) => {
    const origins = value.split(',').map(o => o.trim()).filter(Boolean);
    onFormChange({
      ...form,
      route: {
        ...form.route,
        corsOrigins: origins
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('apis.wizard.policiesStep.title', 'Policies & Security')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('apis.wizard.policiesStep.description', 'Configure authentication, rate limiting, and CORS policies.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Authentication */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('apis.wizard.authentication', 'Authentication')}
          </label>
          <select
            value={form.route.auth}
            onChange={(e) => onFormChange({
              ...form,
              route: { ...form.route, auth: e.target.value as any }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="jwt">JWT (Recommended)</option>
            <option value="key">API Key</option>
            <option value="none">None (Public)</option>
          </select>
        </div>

        {/* Rate Limit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('apis.wizard.rateLimit', 'Rate Limit (per minute)')}
          </label>
          <input
            type="number"
            min="1"
            max="10000"
            value={form.route.rateLimitMinute}
            onChange={(e) => onFormChange({
              ...form,
              route: { ...form.route, rateLimitMinute: parseInt(e.target.value) || 600 }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Request Size Limit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('apis.wizard.requestSizeLimit', 'Request Size Limit (MB)')}
          </label>
          <input
            type="number"
            min="1"
            max="100"
            step="0.1"
            value={form.route.requestSizeMB}
            onChange={(e) => onFormChange({
              ...form,
              route: { ...form.route, requestSizeMB: parseFloat(e.target.value) || 2 }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* CORS Toggle */}
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={form.route.corsEnabled}
              onChange={(e) => onFormChange({
                ...form,
                route: { ...form.route, corsEnabled: e.target.checked }
              })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              {t('apis.wizard.enableCors', 'Enable CORS')}
            </span>
          </label>
        </div>

        {/* CORS Origins */}
        {form.route.corsEnabled && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('apis.wizard.corsOrigins', 'CORS Origins')}
            </label>
            <input
              type="text"
              value={form.route.corsOrigins.join(', ')}
              onChange={(e) => updateCorsOrigins(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="https://app.example.com, https://admin.example.com, *"
            />
            <p className="text-gray-500 text-xs mt-1">
              {t('apis.wizard.corsOriginsHelp', 'Comma-separated list of allowed origins. Use * for all origins (not recommended for production).')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewStep({ 
  form, 
  dryRunResult 
}: { 
  form: CreateApiForm; 
  dryRunResult?: DryRunResponse; 
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('apis.wizard.reviewStep.title', 'Review & Preview')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('apis.wizard.reviewStep.description', 'Review your configuration and see what changes will be made.')}
        </p>
      </div>

      {/* Configuration Summary */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-3">
          {t('apis.wizard.configurationSummary', 'Configuration Summary')}
        </h3>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 text-sm">
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.serviceName', 'Service Name')}:</dt>
            <dd className="text-gray-900">{form.service.name}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.upstream', 'Upstream')}:</dt>
            <dd className="text-gray-900">
              {form.service.protocol}://{form.service.host}:{form.service.port}{form.service.path}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.paths', 'Paths')}:</dt>
            <dd className="text-gray-900">{form.route.paths.join(', ')}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.methods', 'Methods')}:</dt>
            <dd className="text-gray-900">{form.route.methods.join(', ')}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.authentication', 'Authentication')}:</dt>
            <dd className="text-gray-900">{form.route.auth.toUpperCase()}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-600">{t('apis.wizard.rateLimit', 'Rate Limit')}:</dt>
            <dd className="text-gray-900">{form.route.rateLimitMinute}/min</dd>
          </div>
        </dl>
      </div>

      {/* ChangeSet Preview */}
      {dryRunResult && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">
            {t('apis.wizard.changeSetPreview', 'ChangeSet Preview')}
          </h3>
          
          {/* Policy Messages */}
          {dryRunResult.policy.length > 0 && (
            <div className="space-y-2">
              {dryRunResult.policy.map((policy, index) => (
                <div
                  key={index}
                  className={`p-3 rounded ${
                    policy.level === 'error'
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : policy.level === 'warn'
                      ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
                      : 'bg-blue-50 border border-blue-200 text-blue-700'
                  }`}
                >
                  <div className="text-sm font-medium">
                    {policy.level === 'error' ? '⚠️' : policy.level === 'warn' ? '⚠️' : 'ℹ️'} {policy.message}
                  </div>
                  {policy.path && (
                    <div className="text-xs mt-1 opacity-75">
                      Path: {policy.path}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Impact Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              {t('apis.wizard.impact', 'Impact')}
            </h4>
            <p className="text-sm text-blue-700">{dryRunResult.impact.summary}</p>
            {dryRunResult.impact.affected.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-blue-600 font-medium">
                  {t('apis.wizard.affectedResources', 'Affected Resources')}:
                </div>
                <div className="text-xs text-blue-700">
                  {dryRunResult.impact.affected.join(', ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Main wizard component
export default function CreateWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedTenant } = useAuthStore();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState<CreateApiForm>(defaultCreateApiForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const dryRunMutation = useDryRunChangeSet();
  const submitMutation = useSubmitChangeSet();

  const totalSteps = 4;

  const validateCurrentStep = () => {
    const newErrors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!form.service.name.trim()) {
        newErrors.serviceName = t('apis.wizard.errors.serviceNameRequired', 'Service name is required');
      } else if (!/^[a-z0-9-]+$/.test(form.service.name)) {
        newErrors.serviceName = t('apis.wizard.errors.serviceNameInvalid', 'Service name must be lowercase alphanumeric with hyphens');
      }
      if (!form.service.host.trim()) {
        newErrors.serviceHost = t('apis.wizard.errors.serviceHostRequired', 'Service host is required');
      }
    }

    if (currentStep === 2) {
      if (form.route.paths.length === 0 || form.route.paths.every(p => !p.trim())) {
        newErrors.paths = t('apis.wizard.errors.pathsRequired', 'At least one path is required');
      }
      if (form.route.methods.length === 0) {
        newErrors.methods = t('apis.wizard.errors.methodsRequired', 'At least one method is required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;

    if (currentStep === 3) {
      // Trigger dry run before review step
      const changeSet = buildServiceChangeSet(form.service, form.route, selectedTenant || 'dev');
      dryRunMutation.mutate(changeSet);
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    const changeSet = buildServiceChangeSet(form.service, form.route, selectedTenant || 'dev');
    
    try {
      const result = await submitMutation.mutateAsync(changeSet);
      // Redirect to catalog with success message
      navigate('/apis', { 
        state: { 
          message: t('apis.wizard.success', 'API service created successfully! ChangeSet {{id}} has been submitted for approval.', { id: result.id }),
          type: 'success'
        }
      });
    } catch (error) {
      // Handle error
      console.error('Failed to submit ChangeSet:', error);
    }
  };

  const canProceed = () => {
    const hasErrors = dryRunMutation.data?.policy.some(p => p.level === 'error');
    return !hasErrors && !dryRunMutation.isPending && !submitMutation.isPending;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('apis.wizard.title', 'Create API Service')}
        </h1>
        <p className="text-gray-600 mt-1">
          {t('apis.wizard.subtitle', 'Configure a new API service with Kong Gateway')}
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {currentStep === 1 && (
          <ServiceStep form={form} onFormChange={setForm} errors={errors} />
        )}
        {currentStep === 2 && (
          <RouteStep form={form} onFormChange={setForm} errors={errors} />
        )}
        {currentStep === 3 && (
          <PoliciesStep form={form} onFormChange={setForm} />
        )}
        {currentStep === 4 && (
          <ReviewStep form={form} dryRunResult={dryRunMutation.data} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 1}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('apis.wizard.previous', 'Previous')}
        </button>

        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/apis')}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            {t('apis.wizard.cancel', 'Cancel')}
          </button>

          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {currentStep === 3 
                ? t('apis.wizard.reviewAndValidate', 'Review & Validate')
                : t('apis.wizard.next', 'Next')
              }
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed()}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitMutation.isPending
                ? t('apis.wizard.submitting', 'Submitting...')
                : t('apis.wizard.createService', 'Create Service')
              }
            </button>
          )}
        </div>
      </div>

      {/* Loading overlay for dry run */}
      {dryRunMutation.isPending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="animate-spin text-blue-600 text-xl">⟳</div>
              <span className="text-gray-900">
                {t('apis.wizard.validating', 'Validating configuration...')}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
